import { useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Table } from '../../ui';
import { Card, Tag, Button, Alert, Space, Typography, Popconfirm, Tooltip, Modal, Input, Spinner, message } from '../../ui';
import { useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import { request } from '../../api/client';
import { fmtQty } from '../../lib/format';
import { PALETTE } from '../../lib/palette';

/**
 * Fulfillment V2 · Phase 1 — read-only warehouse coverage + guided planning for an order's PENDING qty.
 * Shows every warehouse's product-wise coverage (FULL / PARTIAL / NONE), combined feasibility, and a
 * transfer suggestion. Availability is PHYSICAL on-hand (not reservation-aware yet). All ACTION buttons
 * are gated by permission — the backend is the final authority; a view-only approver sees the matrix but
 * no actions. Actions only call EXISTING endpoints (assign dispatch warehouse / consolidate); it never
 * reserves stock, packs, or touches invoice/payment/ledger/advance-CD.
 */
/**
 * IS THIS ORDER STILL SHORT, RIGHT NOW, WHERE IT ACTUALLY SHIPS FROM?
 *
 * The one definition, exported because the shortfall dialog asks the same question of the same
 * payload and two answers on one screen is exactly the bug this fixes. A shortfall QUEUE ROW is a
 * snapshot — what was short, at the warehouse the order was assigned to when it was raised — so it
 * keeps saying "80 × Biovin 20 kg short at Banglore" long after the order moved to a site that holds
 * all 80. The assigned warehouse's own lines are the present tense; the snapshot is history.
 */
export function liveShortfall(coverage: any): { assigned: any | null; shortLines: any[]; resolved: boolean } {
  const assigned = (coverage?.warehouses ?? []).find((w: any) => w.isAssigned) ?? null;
  const shortLines = (assigned?.lines ?? []).filter((l: any) => Number(l.shortQty) > 0);
  // Nothing left to pack, or the site it ships from covers every pending item.
  const resolved = coverage?.suggestion?.outcome === 'NO_PENDING' || (!!assigned && shortLines.length === 0);
  return { assigned, shortLines, resolved };
}

const COVERAGE: Record<string, { color: string; label: string }> = {
  FULL: { color: 'green', label: 'Full' },
  PARTIAL: { color: 'gold', label: 'Partial' },
  NONE: { color: 'default', label: 'None' },
};

export function WarehouseCoveragePanel({ orderId, dispatchWarehouseId, canPlan, canAddStock, canReportShortfall, orderStatus, transfers, bare, actionsPortal, onChanged }: {
  orderId: string;
  dispatchWarehouseId?: string | null;
  canPlan: boolean;
  canAddStock: boolean;
  canReportShortfall?: boolean;   // Phase 2 — WHM/packer can escalate a shortfall to the planner
  orderStatus?: string;
  transfers?: any[];              // order's linked transfers (attached when it was AWAITING_TRANSFER)
  /** Render WITHOUT the card chrome — for a host that already frames it (the shortfall dialog). */
  bare?: boolean;
  /**
   * Where the action buttons should go, when the host wants them somewhere else.
   *
   * Passing this AT ALL (even as `null`, before the node has mounted) puts the panel in footer mode
   * and keeps the buttons out of the body, so they never flash into view for one frame on their way
   * out. `undefined` — the order page — leaves everything exactly where it has always been.
   */
  actionsPortal?: HTMLElement | null;
  onChanged?: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isFetching, isLoading, isError } = useQuery({
    queryKey: ['order-coverage', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/warehouse-coverage` }),
    retry: false, // a 403 (no coverage permission) should fail fast, not spin
  });
  const [picked, setPicked] = useState<string | undefined>();
  const [reportOpen, setReportOpen] = useState(false);
  const [remarks, setRemarks] = useState('');
  // The OPEN shortfall for this order (if any), so a planner/admin who follows the notification lands on
  // the order and reads the reporter's remark here — not only in the queue page / activity log.
  const canSeeShortfall = !!canReportShortfall || !!canPlan;
  const { data: shortfall } = useQuery({
    queryKey: ['order-shortfall', orderId],
    queryFn: () => request<any>({ url: `/shortfalls/order/${orderId}` }),
    enabled: canSeeShortfall,
    retry: false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['order-coverage', orderId] });
    qc.invalidateQueries({ queryKey: ['order-shortfall', orderId] });
    qc.invalidateQueries({ queryKey: ['order', orderId] });
    qc.invalidateQueries({ queryKey: ['order-timeline', orderId] });
    onChanged?.();
  };
  const onErr = (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed');
  const assign = useConfirmedMutation({ title: "Dispatch this order from the selected warehouse?", content: "Stock is committed there, and the invoice bills under that warehouse’s GST registration.", okText: "Yes, use it" }, {
    mutationFn: (wid: string) => request({ url: `/orders/${orderId}/dispatch-warehouse`, method: 'POST', data: { warehouseId: wid } }),
    onSuccess: () => { message.success('Dispatch warehouse assigned'); refresh(); }, onError: onErr,
  });
  const consolidate = useMutation({
    mutationFn: (wid: string) => request({ url: `/orders/${orderId}/consolidate`, method: 'POST', data: { toWarehouseId: wid } }),
    onSuccess: () => { message.success('Consolidating stock — order held until the transfers are received'); refresh(); }, onError: onErr,
  });
  /*
   * THE TRANSFER PLAN FOR THE SITE THE READER ACTUALLY PICKED.
   *
   * `suggestion.transferPlan` is costed server-side for the SUGGESTED destination alone. So the
   * moment someone ticks a different row, the plan printed on screen described a move into a
   * warehouse nobody had chosen, while the button beside it would raise transfers into the ticked
   * one. Re-ask for the plan (the same endpoint `consolidate` re-runs internally before it writes
   * anything) so the plan, its destination and the button that commits it are one story.
   *
   * Declared before the early returns below — a hook cannot sit behind a conditional — so it reads
   * `data` defensively; with nothing picked, `picked` is undefined and this never fires.
   */
  const suggestedDest = (data as any)?.suggestion?.suggestedDestinationWarehouseId != null
    ? String((data as any).suggestion.suggestedDestinationWarehouseId) : null;
  const planDest = picked && picked !== suggestedDest ? picked : null;
  const { data: pickedPlan, error: pickedPlanError, isFetching: pickedPlanLoading } = useQuery({
    queryKey: ['order-consolidation-plan', orderId, planDest],
    queryFn: () => request<any[]>({ url: `/orders/${orderId}/consolidation-plan`, params: { toWarehouseId: planDest } }),
    enabled: !!planDest && canPlan,
    retry: false,
  });

  const reportShortfall = useConfirmedMutation({ title: "Report a shortfall on this order?", content: "It flags that no warehouse can cover the order as it stands.", okText: "Yes, report it" }, {
    mutationFn: () => request({ url: `/orders/${orderId}/shortfall-report`, method: 'POST', data: { remarks: remarks.trim() || undefined } }),
    onSuccess: () => { message.success('Shortfall reported to the planner'); setReportOpen(false); setRemarks(''); refresh(); }, onError: onErr,
  });

  /* The panel's own frame. A dialog already draws a titled, bordered surface around its content, so
     inside one this renders BARE — a card inside a dialog is two frames around one thing, and the
     dialog's own title has already said what it is. Deliberately a plain function and not a nested
     component: a component declared in render is a new type every render, which would remount the
     whole body (and drop focus out of the remarks box) on every keystroke. */
  const frame = (children: ReactNode, tag?: ReactNode) => (bare
    ? <div className="min-w-0">{tag ? <div className="mb-2.5">{tag}</div> : null}{children}</div>
    : (
      <Card style={{ marginBottom: 16 }}
        // The title has to WRAP on a phone: "Warehouse coverage (plan fulfillment)" is wider than a
        // 360px screen and was being cut off mid-word with no ellipsis.
        title={<span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="break-words">Warehouse coverage (plan fulfillment)</span>
          {tag}
        </span>}>
        {children}
      </Card>
    ));

  if (isLoading) return frame(
    <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>,
  );
  if (isError || !data) return frame(
    <Alert type="warning" title="Warehouse coverage isn't available for you on this order." />,
  );

  const s = data.suggestion ?? {};
  const combined = data.combined ?? {};
  const outcome: string = s.outcome;
  /*
   * SHORT MEANS WHAT THE SERVER MEANS (DEF-054).
   *
   * `shortfallSummary` — which the report endpoint enforces — counts an order short when it needs a transfer, cannot be
   * covered at all, OR its ASSIGNED warehouse cannot cover it even though another single warehouse could
   * (FULLY_COVERED_SINGLE). Reading only the outcome hid "Report shortfall to planner" from the warehouse manager in
   * that last case: GB/2026/09/00235 at Pune (coverage NONE, 15 short) offered no way to escalate, while the
   * partial-pack notice told the planners "Another warehouse can fully cover this".
   */
  const assignedCoverageRow = (data.warehouses ?? []).find((w: any) => w.isAssigned
    || (dispatchWarehouseId != null && String(w.warehouseId) === String(dispatchWarehouseId)));
  const isShort = outcome === 'NEEDS_TRANSFER' || outcome === 'INSUFFICIENT_TOTAL'
    || (outcome !== 'NO_PENDING' && !!assignedCoverageRow && assignedCoverageRow.coverage !== 'FULL');

  // Phase 2 — derived fulfillment sub-status (no DB field): from order status + linked transfers + coverage.
  const tfs: any[] = transfers ?? [];
  const subStatus: { color: string; text: string } | null = (() => {
    if (orderStatus === 'AWAITING_TRANSFER') {
      if (tfs.some((t) => t.status === 'PARTIAL')) return { color: 'red', text: 'Transfer discrepancy — blocked' };
      return { color: 'gold', text: 'Transfer in progress' };
    }
    if (tfs.length && tfs.some((t) => t.status === 'RECEIVED') && tfs.every((t) => ['RECEIVED', 'CANCELLED'].includes(t.status))) {
      return { color: 'green', text: 'Stock received — ready to pack' };
    }
    if (!data.pending?.fullyPacked && isShort) {
      return { color: 'orange', text: 'Short stock' };
    }
    return null;
  })();
  const selected = picked ?? s.suggestedDestinationWarehouseId ?? (dispatchWarehouseId ? String(dispatchWarehouseId) : undefined);
  const selectedRow = (data.warehouses ?? []).find((w: any) => w.warehouseId === selected);

  /*
   * EVERY ACTION ON THIS PANEL TARGETS THE ROW THAT IS TICKED.
   *
   * The banner used to name — and act on — the server's suggestion no matter what the reader had
   * selected, so ticking Delhi left a green button reading "Move to Mumbai Warehouse". The radio had
   * just asked a question and the button was answering a different one; pressing it would have sent
   * the order somewhere nobody had chosen.
   *
   * With nothing picked, `selected` IS the suggestion, so the panel still opens on the recommendation
   * and nothing changes for a reader who simply accepts it. Reassignment is only ever offered for a
   * site that can pack the whole order, so ticking one that cannot fix the shortfall falls back to
   * offering the site that can — a button that named the ticked row there would not help.
   */
  const destRow = selectedRow?.coverage === 'FULL' && !selectedRow.isAssigned
    ? selectedRow
    : (data.warehouses ?? []).find((w: any) => String(w.warehouseId) === String(s.suggestedDestinationWarehouseId)
        && w.coverage === 'FULL' && !w.isAssigned);

  /*
   * IS THIS ORDER STILL SHORT, RIGHT NOW, WHERE IT IS ACTUALLY SHIPPING FROM?
   *
   * Every sentence on this panel used to be written from the QUEUE ROW — a snapshot frozen when the
   * shortfall was raised — and from `suggestion.outcome`, which describes where the stock is, not
   * whether there is still a problem. So an order raised short at Banglore and since moved to
   * Bengaluru (which holds all of it) went on being described as short, under a banner urging the
   * reader to move it somewhere else "rather than waiting for stock" when nothing was being waited
   * for. The snapshot is history; the assigned warehouse's own lines are the present tense.
   */
  const { assigned: assignedRow, shortLines: assignedShort, resolved: nothingShortNow } = liveShortfall(data);

  // The plan, its error and its destination — all for `selected`, whichever row that is.
  const transferPlan: any[] | null = planDest ? ((pickedPlan as any[]) ?? null) : (s.transferPlan ?? null);
  const transferPlanError: string | null = planDest
    ? ((pickedPlanError as any)?.response?.data?.error?.message
      ?? (pickedPlanError ? 'Could not work out a transfer plan into this warehouse.' : null))
    : (s.transferPlanError ?? null);

  // The assigned warehouse is called out BESIDE the name, not on a second line: which warehouse this
  // order is going to ship from is the first question anyone opening this panel has, and a small tag
  // under the name lost that race to ten similar-looking rows.
  // Free = physical − committed. Derived here when the API doesn't send it (an older backend build, or
  // a response cached from before one), because a "Free here" of 0 beside "434 physical / nothing
  // committed" is worse than no column at all — it reads as a stock-out that isn't there.
  // Product-wise counts, derived here as well as server-side so an older/cached payload still reads
  // correctly rather than printing a summed mixed-unit quantity.
  // The GST state master stores names in caps (MAHARASHTRA); a table of shouted place names reads as
  // an error log, so present them the way an address does.
  const placeCase = (v?: string | null) => (v ?? '').toLowerCase().replace(/(^|[\s&/-])([a-z])/g, (_m, p, c) => p + c.toUpperCase());
  const itemsTotal = (w: any) => (w.itemsTotal ?? (w.lines?.length ?? 0));
  const itemsCovered = (w: any) => (w.itemsCovered ?? (w.lines ?? []).filter((l: any) => Number(l.shortQty) <= 0).length);

  const freeOf = (l: any) => (l.freeQty != null
    ? Number(l.freeQty)
    : Math.max(0, Number(l.availableQty ?? 0) - Number(l.committedQty ?? 0)));

  const flags = (w: any) => (
    <Space size={4} wrap>
      {!w.isActive && <Tag tone="red">Inactive</Tag>}
      {w.type === 'PRODUCTION' && <Tag>Production</Tag>}
    </Space>
  );

  return frame(
    <>
      {/* A permanent explanation is a caption, not an alert: three stacked banners made the two that
          actually carry news about THIS order (a reported shortfall, whether it can be fulfilled at
          all) impossible to spot. The full text is one hover away. */}
      <Tooltip label="Physical on-hand across warehouses — not reserved for this order yet. Coverage is decided per product. Expand a warehouse to see what is already committed to other active orders there and what is left free.">
        <div className="mb-2 inline-flex items-center gap-1.5 type-caption text-subtle">
          <Info size={13} />
          {data.meta?.note ?? 'Availability is physical stock only — final packing re-checks it.'}
        </div>
      </Tooltip>

      {shortfall && (() => {
        // WHAT IS SHORT, in the present tense. The queue row's snapshot is what was true when the
        // entry was raised — and at a warehouse the order may no longer ship from — so it is only the
        // fallback for when live coverage cannot be read.
        const snapLines = Array.isArray(shortfall.shortSnapshot) ? shortfall.shortSnapshot
          : (typeof shortfall.shortSnapshot === 'string' ? (() => { try { return JSON.parse(shortfall.shortSnapshot); } catch { return []; } })() : []);
        const lines = assignedShort.length ? assignedShort : snapLines;
        const shortText = lines.length ? lines.map((l: any) => `${fmtQty(l.shortQty)} × ${l.productName ?? l.productId}`).join(', ') : 'short stock';
        // Where the entry was raised, named only when it is NOT where the order ships from now — that
        // difference is the whole explanation for a queue row that looks out of date.
        const raisedElsewhere = shortfall.warehouseName && assignedRow && shortfall.warehouseName !== assignedRow.name
          ? String(shortfall.warehouseName) : null;

        /*
         * THE WAY OUT, offered on the banner itself.
         *
         * A planner arrives here from a shortfall notification and reads one amber box. It used to
         * offer "Add stock" and nothing else, and to say "add stock or arrange production" even when
         * the stock already existed somewhere — sending someone to raise a GRN for goods the company
         * was already holding. The server has ALREADY worked out which of the three situations this
         * order is in (`suggestion.outcome`), so the banner offers the one that applies:
         *
         *   FULLY_COVERED_SINGLE — another warehouse has every pending item → REASSIGN. No transfer
         *     is needed at all, and suggesting one would move stock for nothing.
         *   NEEDS_TRANSFER       — no single warehouse covers it, but the combined stock does → the
         *     consolidate action, which is only ever offered when `transferPlan` holds a real,
         *     costed plan (and `transferPlanError` is clear). That IS "only if transfers can actually
         *     fulfil the requirement" — it is the server's own feasibility verdict, not a guess made
         *     here from the coverage table.
         *   INSUFFICIENT_TOTAL   — the goods do not exist anywhere → add stock / production, the
         *     only honest option, and the one the banner always used to give.
         *
         * All of it is gated on `canPlan`: a packer who can only report the shortfall still sees the
         * sentence explaining what the fix is, but not a button they are not allowed to press.
         */
        const destName = destRow?.name;
        // A fix is only offered while there is something to fix. Reassigning an order that the site
        // it already ships from can pack in full moves goods for nothing.
        const canTransfer = !nothingShortNow && outcome === 'NEEDS_TRANSFER'
          && !!transferPlan?.length && !transferPlanError && !!selected;
        const canReassign = !nothingShortNow && !!destRow;

        // Each case says what is TRUE of this order now, and names the one move that follows from it.
        const fixHint = nothingShortNow
          ? (outcome === 'NO_PENDING'
            ? <>Every item has been packed, so there is nothing left to source.</>
            : <><b>{assignedRow?.name}</b> holds every pending item, so the order can be packed where it is — nothing needs to move.</>)
          : canReassign
            ? <><b>{destName}</b> has every pending item and <b>{assignedRow?.name ?? 'the assigned warehouse'}</b> does not — move the order there rather than waiting for stock.</>
            : canTransfer
              ? <>No single warehouse covers this, but the stock exists across {transferPlan!.length} other{transferPlan!.length === 1 ? '' : 's'} — bring it into <b>{selectedRow?.name}</b> with a transfer.</>
              // The stock IS out there and the combined banner says so; what failed is working out a
              // route for it. Saying "no warehouse has the quantity" here would contradict the green
              // banner two rows down and send the planner to raise stock the company already holds.
              : outcome === 'NEEDS_TRANSFER'
                ? <>The stock exists across several warehouses, but a transfer plan could not be worked out{transferPlanError ? <> — {String(transferPlanError).toLowerCase()}</> : null}. Move it manually, or add stock.</>
                // "No warehouse has the pending quantity" is only true when the COMBINED stock cannot
                // cover the order. Reached with the stock sitting out there — a destination that is
                // not fully covering is ticked, say — it sends a planner to raise goods the company
                // already owns, and contradicts the combined banner below it.
                : combined.canFulfill
                  ? <>The stock exists across the network, but no single warehouse covers this order on its own — pick a destination in the table below to bring it in.</>
                  : 'No warehouse has the pending quantity — add stock or arrange production.';

        return (
          <Alert
            // A solved problem is not a warning. The box keeps its place (the reader followed a
            // notification here and needs to know what became of it) but stops shouting.
            type={nothingShortNow ? 'success' : 'warning'}
            style={{ marginBottom: 12 }}
            title={nothingShortNow
              ? <span>No longer short{assignedRow ? <> — <b>{assignedRow.name}</b> can pack this order in full</> : null}</span>
              // Say HOW it was raised: only a MANUAL report has a person behind it. Since approval queues a
              // short order by itself, "reported by a colleague" on an AUTO row named a report nobody made.
              : shortfall.reportedVia === 'MANUAL'
                ? <span>Shortfall reported{shortfall.reportedByName ? ` by ${shortfall.reportedByName}` : ''} — short {shortText}</span>
                : shortfall.reportedVia === 'AUTO_PACK'
                  ? <span>Short when packing — short {shortText}</span>
                  : shortfall.reportedVia === 'AUTO_DISPATCH'
                    ? <span>Short when dispatching — short {shortText}</span>
                    : <span>Short at {shortfall.warehouseName ?? 'the assigned warehouse'} — flagged automatically — short {shortText}</span>}
            description={<>
              {shortfall.remarks ? <div style={{ fontStyle: 'italic' }}>“{shortfall.remarks}”</div> : null}
              <div>{fixHint}</div>
              {/* Why a queue entry is still open against an order that is fine: it was raised at a
                  different site, and it clears itself the next time the order is re-checked. */}
              {nothingShortNow && (
                <div className="mt-1 type-caption text-muted">
                  {raisedElsewhere
                    ? <>The entry was raised at <b>{raisedElsewhere}</b>, before the order moved here. It clears itself once the order is re-checked.</>
                    : <>The queue entry clears itself once the order is re-checked.</>}
                </div>
              )}
            </>}
            action={(
              <Space wrap>
                {canPlan && canReassign && (
                  <Button size="sm" variant="primary" loading={assign.isPending}
                    onClick={() => assign.mutate(String(destRow!.warehouseId))}>
                    Move to {destName}
                  </Button>
                )}
                {canPlan && canTransfer && (
                  <Popconfirm
                    title="Transfer the shortfall in?"
                    description="Raises the transfers in the plan below; the order is held until they are received."
                    okText="Transfer" onConfirm={() => consolidate.mutate(String(selected))}
                  >
                    <Button size="sm" variant="primary" loading={consolidate.isPending}>Transfer stock in</Button>
                  </Popconfirm>
                )}
                {/* Nothing is short, so there is nothing to add — an action offered here would be
                    a suggestion to solve a problem the banner has just said does not exist. */}
                {canAddStock && !nothingShortNow && (
                  <Button size="sm" variant="default" onClick={() => navigate('/inventory/master-stock')}>Add stock</Button>
                )}
              </Space>
            )}
          />
        );
      })()}

      {outcome === 'NO_PENDING' ? (
        <Alert type="success" title="This order is fully packed — nothing left to fulfill." />
      ) : (
        <>
          {/* Combined feasibility banner */}
          {/* Same rule as the table: count ITEMS, never a summed quantity across different units. */}
          {(() => {
            const byP: any[] = combined.byProduct ?? [];
            const shortP = byP.filter((p: any) => Number(p.shortQty) > 0);
            // The shortfall banner directly above has ALREADY said the assigned warehouse holds the
            // lot. Two stacked green boxes making the same statement in slightly different words read
            // as two findings, and the reader looks for the difference between them. There is none.
            if (shortfall && nothingShortNow && combined.canFulfill) return null;
            return (
              <Alert style={{ marginBottom: 12 }} type={combined.canFulfill ? 'success' : 'error'}
                // "Combined across all warehouses" is the answer to "can this be fulfilled AT ALL",
                // and it is the wrong sentence when the order's own warehouse already holds the lot:
                // it credits a dozen sites for stock that is sitting at one, and implies something
                // has to be brought together when nothing does.
                title={combined.canFulfill
                  ? (nothingShortNow && assignedRow
                    ? `${assignedRow.name} holds every pending item${byP.length > 1 ? ` (all ${byP.length} items)` : ''} — this order can be packed where it is.`
                    : `Combined across all warehouses, every item is available — the order can be fully fulfilled${byP.length > 1 ? ` (all ${byP.length} items)` : ''}.`)
                  : `Combined across all warehouses, ${shortP.length} of ${byP.length} item${byP.length === 1 ? '' : 's'} cannot be covered — there is not enough stock anywhere.`}
                description={!combined.canFulfill && shortP.length
                  ? shortP.map((p: any) => `${p.productName ?? p.productId}: short ${fmtQty(p.shortQty)} of ${fmtQty(p.requiredQty)}`).join(' · ')
                  : undefined} />
            );
          })()}

          <Table size="small" loading={isFetching} pagination={false} rowKey="warehouseId" dataSource={data.warehouses ?? []}
            rowSelection={canPlan ? { type: 'radio', selectedRowKeys: selected ? [selected] : [], onChange: (k) => setPicked(String(k[0])) } : undefined}
            // Highlight the ASSIGNED row, not every row with full coverage — five highlighted rows told
            // the reader nothing, and the one fact they needed was which of them is actually shipping.
            // `bg-brand-50` is a LIGHT wash and has no dark-mode counterpart, so in dark mode the one
            // row the reader is looking for came out as a near-white band with unreadable text on it.
            rowClassName={(w: any) => (w.isAssigned ? 'bg-brand-50 dark:bg-brand-400/[0.12]' : '')}
            // autoFit: size each column to its content and SHARE the leftover width. Without it the
            // three numeric columns took a fixed default each and the warehouse column soaked up every
            // remaining pixel of a full-width panel — the acres of blank space between the name and
            // the numbers. Short values under long headings is exactly what autoFit is for.
            autoFit
            columns={[
              { title: 'Warehouse', render: (_: any, w: any) => (
                <div>
                  <b>{w.name}</b> <span style={{ color: 'var(--gb-text-muted)' }}>{w.code ? `(${w.code})` : ''}</span>
                  {w.isAssigned && <Tag tone="green" className="ml-2">Assigned to this order</Tag>}
                  <div>{flags(w)}</div>
                </div>
              ) },
              // Where it ships FROM. Freight and tax both follow from it, and a warehouse code doesn't
              // say it — WH-BLR being in Karnataka is exactly why this order would be billed IGST.
              { title: 'Ships from', render: (_: any, w: any) => (
                <span className="text-sm text-muted">{[w.city, placeCase(w.stateName) || w.stateCode].filter(Boolean).join(', ') || '—'}</span>
              ) },
              // The tax head is a CONSEQUENCE of this choice, so it is shown while the choice is open
              // rather than discovered on the invoice afterwards.
              { title: 'GST', align: 'center' as const, render: (_: any, w: any) => (w.taxHeads
                ? <Tooltip label={w.taxNature === 'INTER_STATE'
                    ? `Supplied from ${placeCase(w.supplierStateName ?? w.stateName)} to a different state — inter-state, so IGST.`
                    : `Supplied from ${placeCase(w.supplierStateName ?? w.stateName)} within the same state — so the tax splits into two heads.`}>
                    <Tag tone={w.taxNature === 'INTER_STATE' ? 'gold' : 'green'}>{w.taxHeads}</Tag>
                  </Tooltip>
                : <Tooltip label="This warehouse has no usable GST registration, so the tax head can't be worked out for it."><span className="text-subtle">—</span></Tooltip>) },
              { title: 'Coverage', align: 'center' as const, render: (_: any, w: any) => <Tag color={COVERAGE[w.coverage]?.color}>{COVERAGE[w.coverage]?.label ?? w.coverage}</Tag> },
              // COUNTS, not a summed quantity. On a multi-product order "43 of 45" adds litres to
              // packets and means nothing; "2 of 3 items" is true whatever the units are. The
              // quantities themselves live per product, one row down, where they have a unit.
              { title: 'Items covered', align: 'center' as const, render: (_: any, w: any) => {
                const total = itemsTotal(w); const covered = itemsCovered(w);
                return (
                  <Tooltip label={(w.lines ?? []).map((l: any) => `${l.productName ?? l.productId}: ${fmtQty(l.availableQty)} here, ${fmtQty(l.requiredQty)} needed`).join(' · ')}>
                    <span style={{ color: covered < total ? PALETTE.warning[600] : undefined }}>{covered} of {total}</span>
                  </Tooltip>
                );
              } },
              { title: 'Short', align: 'center' as const, render: (_: any, w: any) => {
                const shortLines = (w.lines ?? []).filter((l: any) => Number(l.shortQty) > 0);
                if (!shortLines.length) return '—';
                return (
                  <Tooltip label={shortLines.map((l: any) => `${l.productName ?? l.productId} short ${fmtQty(l.shortQty)}`).join(' · ')}>
                    <b style={{ color: 'var(--color-warning-600)' }}>
                      {shortLines.length === 1 ? `${fmtQty(shortLines[0].shortQty)} short` : `${shortLines.length} items`}
                    </b>
                  </Tooltip>
                );
              } },
            ]}
            expandable={{
              rowExpandable: (w: any) => (w.lines?.length ?? 0) > 0,
              // The whole row opens it, not just the chevron — the row is what the reader aims at, and
              // the selection cell stops its own propagation so picking a warehouse still just picks it.
              expandRowByClick: true,
              expandedRowRender: (w: any) => (
                // Physical → committed → free, the same three numbers (and the same wording) the approve
                // screen shows, so a planner reading both screens is reading one vocabulary. Coverage and
                // Short above stay PHYSICAL — committed stock is advisory, nothing is reserved — but a
                // warehouse whose free stock no longer covers the line is called out in amber here, which
                // is the case a "Full" coverage tag on its own would have hidden.
                <Table size="small" pagination={false} rowKey="productId" dataSource={w.lines} autoFit
                  columns={[
                    { title: 'Product', render: (_: any, l: any) => l.productName ?? l.productId },
                    { title: 'Required', align: 'center' as const, render: (_: any, l: any) => fmtQty(l.requiredQty) },
                    { title: 'Physical here', align: 'center' as const, render: (_: any, l: any) => <span style={{ color: l.shortQty > 0 ? PALETTE.warning[600] : undefined }}>{fmtQty(l.availableQty)}</span> },
                    { title: 'Committed', align: 'center' as const, render: (_: any, l: any) => (Number(l.committedQty) > 0
                      ? <Tooltip label="Pending quantity of other active orders already assigned to this warehouse. Advisory — nothing is reserved."><span style={{ color: 'var(--color-warning-600)' }}>−{fmtQty(l.committedQty)}</span></Tooltip>
                      : '—') },
                    { title: 'Free here', align: 'center' as const, render: (_: any, l: any) => (
                      <b style={{ color: freeOf(l) < Number(l.requiredQty) ? PALETTE.warning[600] : PALETTE.success[600] }}>{fmtQty(freeOf(l))}</b>
                    ) },
                    { title: 'Short', align: 'center' as const, render: (_: any, l: any) => (l.shortQty > 0 ? <b style={{ color: 'var(--color-warning-600)' }}>{fmtQty(l.shortQty)}</b> : '—') },
                  ]} />
              ),
            }} />

          {/* Transfer suggestion (NEEDS_TRANSFER) */}
          {outcome === 'NEEDS_TRANSFER' && (
            <div style={{ marginTop: 12 }}>
              {pickedPlanLoading ? (
                <Alert type="info" title={`Working out how to get the shortfall into ${selectedRow?.name ?? 'the selected warehouse'}…`} />
              ) : transferPlanError ? (
                <Alert type="warning" title="Transfer plan could not be generated; a planner must review manually." description={transferPlanError} />
              ) : (transferPlan?.length ? (
                <Alert type="warning"
                  title={`No single warehouse covers this order, but the combined stock does — transfer the shortfall into ${selectedRow?.name ?? 'the destination'} and ship as one.`}
                  description={<div>{transferPlan.map((p: any) => (
                    <div key={p.fromWarehouseId} style={{ fontSize: 13 }}>• <b>{p.fromName}</b> → {selectedRow?.name}: {p.lines.map((l: any) => `${l.productName ?? l.productId} ×${fmtQty(l.qty)}`).join(', ')}</div>
                  ))}</div>} />
              ) : null)}
            </div>
          )}

          {outcome === 'INSUFFICIENT_TOTAL' && (
            <Alert style={{ marginTop: 12 }} type="error"
              title={`Not enough stock across all warehouses (short ${fmtQty(combined.totalShort)}).`}
              description="Options: add stock (GRN/production), assign a warehouse and dispatch what's available now (partial), or assign and wait for full stock." />
          )}

          {/*
            * ACTIONS — permission-gated; the backend is the final authority.
            *
            * In a DIALOG they belong on the sticky footer, not at the bottom of the body: the matrix
            * is the tallest thing on the screen, so a reader who scrolled it to tick a warehouse had
            * to scroll back down past it to reach the button that acts on their choice. `actionsPortal`
            * moves the buttons there — same React tree, same state, same mutations, only the DOM
            * position differs. The view-only note is prose, not an action, so it stays in the body.
            */}
          {(() => {
            const planActions = canPlan ? (
              <Space wrap>
                {selectedRow?.coverage === 'FULL' && (
                  <Button variant="primary" loading={assign.isPending} disabled={selectedRow.isAssigned}
                    onClick={() => assign.mutate(selected!)}>
                    {selectedRow.isAssigned ? 'Assigned (covers fully)' : `Assign ${selectedRow.name}`}
                  </Button>
                )}
                {outcome === 'NEEDS_TRANSFER' && selected && (
                  <Popconfirm title="Consolidate & hold?" description="Transfers the shortfall in; the order is held until they are received."
                    okText="Consolidate" onConfirm={() => consolidate.mutate(selected)} disabled={!!transferPlanError || !(transferPlan?.length)}>
                    <Button variant="primary" icon={undefined} loading={consolidate.isPending || pickedPlanLoading} disabled={!!transferPlanError || !(transferPlan?.length)}>
                      Consolidate &amp; hold order
                    </Button>
                  </Popconfirm>
                )}
                {selected && selectedRow?.coverage !== 'FULL' && (
                  <>
                    <Tooltip label="Assign this warehouse now; then use Pick &amp; pack to ship what's available. The rest stays pending.">
                      <Button variant="default" loading={assign.isPending} disabled={selectedRow?.isAssigned} onClick={() => assign.mutate(selected)}>Assign &amp; pack available now</Button>
                    </Tooltip>
                    <Tooltip label="Assign this warehouse and wait for stock to arrive (transfer / production) before packing.">
                      <Button variant="default" loading={assign.isPending} disabled={selectedRow?.isAssigned} onClick={() => assign.mutate(selected)}>Assign &amp; wait for stock</Button>
                    </Tooltip>
                  </>
                )}
                {canAddStock && !combined.canFulfill && (
                  <Tooltip label="Add opening stock at the master warehouse (a goods-receipt note is created automatically), then transfer it out.">
                    <Button variant="default" onClick={() => navigate('/inventory/master-stock')}>Add stock</Button>
                  </Tooltip>
                )}
              </Space>
            ) : null;
            const viewOnlyNote = canPlan ? null : (
              <Typography.Text type="secondary">You can view coverage. A planner (dispatch/edit permission) assigns the warehouse or arranges a transfer.</Typography.Text>
            );
            /*
             * "Report shortfall to planner" is for the one case a report adds anything: a packer who cannot
             * fix it, on an order NO single warehouse can fill (it needs a transfer or new stock).
             *   • A planner can fix it right here (assign / transfer) — reporting it to themselves is noise.
             *   • When another warehouse holds the WHOLE order the fix is obvious and the approval has
             *     already put it on the planner's shortfall queue, so the packer is told that instead of
             *     being handed a red button (owner, 26 Sep: "if stock is fully available in a WH, why
             *     show Report shortfall?"). Supersedes the DEF-054 "always offer it" reading.
             */
            const fullElsewhere = (data.warehouses ?? []).find((w: any) => w.coverage === 'FULL' && !w.isAssigned
              && !(dispatchWarehouseId != null && String(w.warehouseId) === String(dispatchWarehouseId)));
            // Stock already on its way in (AWAITING_TRANSFER) IS the planner's answer — nothing to report, nothing
            // queued; the transfer status line above says what is happening.
            const reportable = canReportShortfall && isShort && !canPlan && orderStatus !== 'AWAITING_TRANSFER';
            const reportAction = reportable && !fullElsewhere ? (
              <Button variant="danger" onClick={() => setReportOpen(true)}>Report shortfall to planner</Button>
            ) : reportable && fullElsewhere ? (
              <Typography.Text type="secondary">
                {fullElsewhere.name} holds this whole order — a planner has it in the shortfall queue to move it there.
              </Typography.Text>
            ) : null;

            if (actionsPortal !== undefined) {
              return (
                <>
                  {viewOnlyNote && <div style={{ marginTop: 14 }}>{viewOnlyNote}</div>}
                  {actionsPortal && createPortal(
                    <div className="flex flex-wrap items-center gap-2">{planActions}{reportAction}</div>,
                    actionsPortal,
                  )}
                </>
              );
            }
            return (
              <div style={{ marginTop: 14 }}>
                {planActions ?? viewOnlyNote}
                {reportAction && <div style={{ marginTop: 8 }}>{reportAction}</div>}
              </div>
            );
          })()}
        </>
      )}

      <Modal
        open={reportOpen}
        title="Report shortfall to planner"
        onOpenChange={(o) => { if (!o) setReportOpen(false); }}
        footer={<>
          <Button variant="default" onClick={() => setReportOpen(false)}>Cancel</Button>
          <Button variant="primary" loading={reportShortfall.isPending} onClick={() => reportShortfall.mutate()}>Report shortfall</Button>
        </>}
      >
        <Typography.Paragraph type="secondary">
          This alerts Admin/planners that this order is short at the assigned warehouse so they can arrange a
          transfer or add stock. The short quantities are taken from live coverage — you don't enter them.
        </Typography.Paragraph>
        <Input.TextArea rows={3} maxLength={500} placeholder="Optional remarks for the planner (e.g. urgency, customer commitment)…"
          value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </Modal>
    </>,
    subStatus ? <Tag color={subStatus.color}>{subStatus.text}</Tag> : null,
  );
}
